import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { useHalloween } from "./HalloweenProvider";

export default function HalloweenAdminTab() {
  const { enabled, banner_text, save } = useHalloween();
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState(banner_text);

  useEffect(() => setDraft(banner_text), [banner_text]);

  const run = async (partial, okMsg) => {
    setBusy(true);
    try {
      await save(partial);
      toast.success(okMsg);
    } catch {
      toast.error("Could not update theme");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-2xl space-y-8" data-testid="theme-tab">
      <div>
        <h3 className="font-display uppercase tracking-tight text-lg mb-2">Seasonal theme</h3>
        <p className="text-gray-400 text-sm mb-6">
          Dresses the storefront in a Halloween look — glowing purple/orange accents, fog, haunted skyline, and flying bats.
          Layout, products, and checkout stay exactly the same. Turn it off any time.
        </p>
        <div className="flex items-center justify-between gap-6 border border-white/10 bg-white/[0.02] px-5 py-4">
          <div>
            <p className="font-display uppercase tracking-widest text-sm">Halloween theme</p>
            <p className={`text-xs mt-1 ${enabled ? "text-[var(--hk-orange)]" : "text-gray-500"}`} data-testid="theme-halloween-status">
              {enabled ? "ON — visible to all visitors" : "OFF"}
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={enabled}
            disabled={busy}
            onClick={() => run({ halloween_enabled: !enabled }, !enabled ? "Halloween theme is live" : "Halloween theme turned off")}
            data-testid="theme-halloween-toggle"
            className={`relative w-14 h-8 rounded-full transition-colors disabled:opacity-60 ${
              enabled ? "bg-[var(--hk-orange)]" : "bg-white/15"
            }`}
          >
            <span className={`absolute top-1 left-1 w-6 h-6 rounded-full bg-white transition-transform ${enabled ? "translate-x-6" : ""}`} />
          </button>
        </div>
      </div>

      <div>
        <h3 className="font-display uppercase tracking-tight text-lg mb-2">Seasonal headline</h3>
        <p className="text-gray-400 text-sm mb-4">
          A short glowing line shown above the home hero while the theme is on. Leave empty to hide it.
        </p>
        <div className="border border-white/10 bg-white/[0.02] p-5 space-y-4">
          <input
            data-testid="theme-banner-input"
            value={draft}
            maxLength={120}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="e.g. THE HAUNTED DROP LANDS 10.31 — LIMITED BATCH"
            className="w-full bg-black/40 border border-white/10 px-4 py-3 text-sm text-white placeholder:text-gray-600 focus:outline-none focus:border-[var(--hk-orange)]"
          />
          <div className="flex items-center justify-between gap-4">
            <span className="text-xs text-gray-500">{draft.length}/120</span>
            <div className="flex gap-2">
              {banner_text && (
                <button
                  type="button"
                  data-testid="theme-banner-clear"
                  disabled={busy}
                  onClick={() => run({ banner_text: "" }, "Headline removed")}
                  className="px-4 py-2 text-xs font-display uppercase tracking-widest border border-white/15 text-gray-300 hover:text-white hover:border-white/40 disabled:opacity-60"
                >
                  Remove
                </button>
              )}
              <button
                type="button"
                data-testid="theme-banner-save"
                disabled={busy || draft.trim() === banner_text}
                onClick={() => run({ banner_text: draft.trim() }, "Headline saved")}
                className="px-5 py-2 text-xs font-display font-bold uppercase tracking-widest bg-[var(--hk-orange)] text-black hover:bg-[var(--hk-orange-hover)] disabled:opacity-50"
              >
                Save headline
              </button>
            </div>
          </div>
          {banner_text && (
            <p className="text-xs text-gray-500" data-testid="theme-banner-current">
              Live now: <span className="text-[var(--hk-orange)]">{banner_text}</span>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
